/**
 * Pending Pericias PDF Creator - Converted to use @react-pdf/renderer
 *
 * This component has been converted from the old jsPDF-based PDF generation
 * to use the new @react-pdf/renderer library, following the same pattern as
 * pdf.component.tsx and table-pdf-component.tsx.
 *
 * Changes made:
 * - Replaced jsPDF with @react-pdf/renderer
 * - Created PendingPericiasPDFDocument component using Document/Page/View/Text
 * - Replaced jsPDF text positioning with react-pdf styling
 * - Used PDFDownloadLink instead of manual PDF generation
 * - Commented out old jsPDF-based implementation for future reference
 *
 * Note: @react-pdf/renderer package needs to be installed
 * Run: npm install @react-pdf/renderer
 */

import React from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";
import { PericiaInvoiceItem, getPericiaCustomPriceCurrencyFormat } from "../../../../entities/pericia/model/pericia";
import { Costumer } from "../../../../entities/costumer";
import { currencyMask, formatIntegerToCurrency } from "../../../../shared/lib/numbers";

const nuovautoIMG = require("../../../../assets/nuovauto.png");

// Register font for better typography
Font.register({
  family: "Helvetica",
  src: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.53/fonts/Helvetica.ttf"
});

interface PendingPericiasPDFProps {
  pericias: PericiaInvoiceItem[];
  costumer: Costumer;
  start: Date;
  end: Date;
  totalPrice: number;
  displayFields: SelectedFieldsToDisplay;
  periciaCount: number; // Add stable count prop
}

interface SelectedFieldsToDisplay {
  displayPlate: boolean;
  displayChassis: boolean;
  displayDate: boolean;
  displayPrice: boolean;
  displayUserName: boolean;
  displayTotalHours: boolean;
}

// PDF styles using react-pdf - Professional layout matching table component
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 12,
    padding: 32,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4
  },
  pageBorder: {
    position: "absolute",
    top: 15,
    bottom: 15,
    left: 15,
    right: 15,
    borderWidth: 2,
    borderColor: "#000",
    borderStyle: "solid"
  },
  header: {
    marginBottom: 15,
    alignItems: "flex-start"
  },
  logo: {
    width: 180,
    height: 60,
    alignSelf: "center",
    marginBottom: 20
  },
  customerInfo: {
    marginBottom: 20,
    fontSize: 10
  },
  flexRow: {
    flexDirection: "row",
    marginBottom: 8
  },
  customerCol: {
    width: "50%",
    textAlign: "left"
  },
  boldLabel: {
    fontWeight: "bold"
  },
  generatedDate: {
    fontSize: 10,
    marginBottom: 15,
    textAlign: "center"
  },
  tableContainer: {
    marginTop: 15
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingBottom: 5,
    marginBottom: 10,
    fontSize: 10,
    fontWeight: "bold"
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: "#ccc",
    paddingVertical: 5,
    fontSize: 9
  },
  // Dynamic column widths based on displayed fields
  colMacchina: {
    width: "20%",
    textAlign: "left"
  },
  colTarga: {
    width: "18%",
    textAlign: "left"
  },
  colData: {
    width: "15%",
    textAlign: "left"
  },
  colTecnico: {
    width: "18%",
    textAlign: "left"
  },
  colPrezzo: {
    width: "15%",
    textAlign: "left"
  },
  colOre: {
    width: "14%",
    textAlign: "left"
  },
  // Wider columns when fewer fields are displayed
  colMacchinaWide: {
    width: "30%",
    textAlign: "left"
  },
  colTargaWide: {
    width: "25%",
    textAlign: "left"
  },
  colDataWide: {
    width: "20%",
    textAlign: "left"
  },
  colTecnicoWide: {
    width: "25%",
    textAlign: "left"
  },
  totalSection: {
    marginTop: 20,
    alignItems: "flex-end"
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderColor: "#000",
    paddingTop: 5,
    width: 200,
    fontWeight: "bold"
  }
});

// Helper function to get column styles based on number of displayed fields
const getColumnStyles = (displayFields: SelectedFieldsToDisplay) => {
  const fieldCount = Object.values(displayFields).filter(Boolean).length;
  const isWide = fieldCount <= 3;

  return {
    macchina: isWide ? styles.colMacchinaWide : styles.colMacchina,
    targa: isWide ? styles.colTargaWide : styles.colTarga,
    data: isWide ? styles.colDataWide : styles.colData,
    tecnico: isWide ? styles.colTecnicoWide : styles.colTecnico,
    prezzo: styles.colPrezzo,
    ore: styles.colOre
  };
};

// PDF Document Component
const PendingPericiasPDFDocument: React.FC<PendingPericiasPDFProps> = ({ pericias, costumer, start, end, totalPrice, displayFields, periciaCount }) => {
  // Safely handle data
  const safePericias = pericias || [];
  const safeCostumer = costumer || { name: "Unknown Customer" };
  const safeStart = start || new Date();
  const safeEnd = end || new Date();
  const safeTotalPrice = totalPrice || 0;
  const safePericiaCount = periciaCount || 0; // Use stable count prop
  const safeDisplayFields = displayFields || {
    displayPlate: true,
    displayChassis: false,
    displayDate: true,
    displayPrice: true,
    displayUserName: true,
    displayTotalHours: true
  };

  try {
    const columnStyles = getColumnStyles(safeDisplayFields);

    return (
      <Document>
        <Page size="A4" style={styles.page} wrap>
          {/* Paint each physical page independently of the flowing table. */}
          <View style={styles.pageBorder} fixed />
          {/* Header with Logo - only on first page */}
          <View wrap={false}>
            <View style={styles.header}>
              <Image src={nuovautoIMG} style={styles.logo} />
            </View>

            {/* Customer Information Section */}
            <View style={styles.customerInfo}>
              <View style={styles.flexRow}>
                <Text style={styles.customerCol}>
                  <Text style={styles.boldLabel}>Cliente:</Text> {safeCostumer.name}
                </Text>
                <Text style={styles.customerCol}>
                  <Text style={styles.boldLabel}>Periodo:</Text> {safeStart.toLocaleDateString("pt-BR")} -{" "}
                  {safeEnd.toLocaleDateString("pt-BR")}
                </Text>
              </View>
              <View style={styles.flexRow}>
                <Text style={styles.customerCol}>
                  <Text style={styles.boldLabel}>Totale di Perizie:</Text> {safePericiaCount}
                </Text>
                {safeDisplayFields.displayPrice && (
                  <Text style={styles.customerCol}>
                    <Text style={styles.boldLabel}>Valore Totale:</Text> {currencyMask(formatIntegerToCurrency(safeTotalPrice))}
                  </Text>
                )}
              </View>
            </View>

            <Text style={styles.generatedDate}>Generato in: {new Date().toLocaleDateString("pt-BR")}</Text>
          </View>

          {/* Table */}
          <View style={styles.tableContainer}>
            {/* Table Header */}
            <View style={styles.tableHeader} fixed>
              <Text style={columnStyles.macchina}>Macchina</Text>
              {safeDisplayFields.displayPlate && <Text style={columnStyles.targa}>Targa (Telaio)</Text>}
              {safeDisplayFields.displayDate && <Text style={columnStyles.data}>Data</Text>}
              {safeDisplayFields.displayUserName && <Text style={columnStyles.tecnico}>Tecnico</Text>}
              {safeDisplayFields.displayPrice && <Text style={columnStyles.prezzo}>Prezzo</Text>}
              {safeDisplayFields.displayTotalHours && <Text style={columnStyles.ore}>Ore</Text>}
            </View>

            {/* Table Rows - with error handling for each item */}
            {safePericias.map((item, itemIndex) => {
              // Keep enough space after the last item for the final total.
              const minPresenceAhead = safeDisplayFields.displayPrice && itemIndex === safePericias.length - 1 ? 48 : 0;
              try {
                const { car } = item;
                const { brand = "", model = "", plate = "", chassisNumber = "" } = car || {};
                const chassis = chassisNumber.length > 2 ? chassisNumber : "-";
                const carIdentifier = `${brand} ${model}`;

                return (
                  <View key={itemIndex} style={styles.tableRow} wrap={false} minPresenceAhead={minPresenceAhead}>
                    <Text style={columnStyles.macchina}>{carIdentifier.slice(0, 20)}</Text>
                    {safeDisplayFields.displayPlate && (
                      <Text style={columnStyles.targa}>
                        {plate} ({chassis})
                      </Text>
                    )}
                    {safeDisplayFields.displayDate && (
                      <Text style={columnStyles.data}>{item.date?.toLocaleDateString("pt-BR") || "-"}</Text>
                    )}
                    {safeDisplayFields.displayUserName && (
                      <Text style={columnStyles.tecnico}>{item.repairRecords?.[0]?.user?.display_name || "-"}</Text>
                    )}
                    {safeDisplayFields.displayPrice && (
                      <Text style={columnStyles.prezzo}>
                        {(() => {
                          try {
                            return getPericiaCustomPriceCurrencyFormat(item);
                          } catch (error) {
                            console.log("Error formatting price for item:", item.id, error);
                            return "€0,00";
                          }
                        })()}
                      </Text>
                    )}
                    {safeDisplayFields.displayTotalHours && <Text style={columnStyles.ore}>{item.totalHours || 0}</Text>}
                  </View>
                );
              } catch (error) {
                console.log(`Error rendering item ${itemIndex}:`, error);
                return (
                  <View key={itemIndex} style={styles.tableRow} wrap={false} minPresenceAhead={minPresenceAhead}>
                    <Text style={columnStyles.macchina}>Error loading item</Text>
                  </View>
                );
              }
            })}

            {/* Total on last page */}
            {safeDisplayFields.displayPrice && (
              <View style={styles.totalSection} wrap={false}>
                <View style={styles.totalRow}>
                  <Text>Totale:</Text>
                  <Text>{currencyMask(formatIntegerToCurrency(safeTotalPrice))}</Text>
                </View>
              </View>
            )}
          </View>
        </Page>
      </Document>
    );
  } catch (error) {
    console.error("Error rendering PendingPericiasPDFDocument:", error);
    // Return a simple error document
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View>
            <Text style={styles.boldLabel}>Error generating PDF. Check console for details.</Text>
          </View>
        </Page>
      </Document>
    );
  }
};

export type { PendingPericiasPDFProps, SelectedFieldsToDisplay };
export { PendingPericiasPDFDocument };
